// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Audit Log Viewer (M7 — V·IANet Audit)
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../i18n';
import { C } from '../../../../lib/design';
import { Ic, ICON_PATHS } from '../../../../lib/ui';
import { AppHeader } from '../../../shared/components/AppHeader';
import {
  fetchAuditoria,
  fetchAuditoriaEntidades,
  fetchAuditoriaAcciones,
  type AuditLogEntry,
  type AuditoriaParams,
} from '../../../shared/services/api';

// ─── Action color coding ───
const ACTION_STYLES: Record<string, { color: string; bg: string }> = {
  CREAR_DOCUMENTO: { color: '#16A34A', bg: '#F0FDF4' },
  EDITAR_DOCUMENTO: { color: '#2563EB', bg: '#EFF6FF' },
  ENVIAR_A_REVISION: { color: '#F59E0B', bg: '#FFFBEB' },
  APROBAR_VERSION: { color: '#16A34A', bg: '#F0FDF4' },
  RECHAZAR_VERSION: { color: '#DC2626', bg: '#FEF2F2' },
  PUBLICAR_DOCUMENTO: { color: '#16A34A', bg: '#F0FDF4' },
  DAR_DE_BAJA: { color: '#DC2626', bg: '#FEF2F2' },
  CREAR_REFERENCIA: { color: '#2563EB', bg: '#EFF6FF' },
  ELIMINAR_REFERENCIA: { color: '#DC2626', bg: '#FEF2F2' },
  INICIAR_CONVERSACION: { color: '#8B5CF6', bg: '#F5F3FF' },
  LOGIN: { color: '#6B7280', bg: '#F9FAFB' },
  LOGOUT: { color: '#6B7280', bg: '#F9FAFB' },
};

type AuditActions = typeof import('../../../../i18n/es').es.audit.actions;

function getActionLabels(actions: AuditActions): Record<string, string> {
  return {
    CREAR_DOCUMENTO: actions.createDoc,
    EDITAR_DOCUMENTO: actions.editDoc,
    ENVIAR_A_REVISION: actions.sendReview,
    APROBAR_VERSION: actions.approveVersion,
    RECHAZAR_VERSION: actions.rejectVersion,
    PUBLICAR_DOCUMENTO: actions.publishDoc,
    DAR_DE_BAJA: actions.deactivate,
    CREAR_REFERENCIA: actions.createRef,
    ELIMINAR_REFERENCIA: actions.deleteRef,
    INICIAR_CONVERSACION: actions.startChat,
    LOGIN: actions.login,
    LOGOUT: actions.logout,
  };
}

function getActionConfig(evento: string, labels: Record<string, string>) {
  const style = ACTION_STYLES[evento] ?? { color: '#6B7280', bg: '#F9FAFB' };
  return { ...style, label: labels[evento] ?? evento };
}

// ─── Format timestamp ───
function formatTimestamp(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

// ─── Detail summary ───
function formatDetalle(entry: AuditLogEntry): string {
  if (!entry.detalle) return '—';
  const d = entry.detalle as Record<string, string | string[] | undefined>;
  const parts: string[] = [];
  if (d.codigo) parts.push(d.codigo as string);
  if (d.titulo) parts.push(d.titulo as string);
  if (d.nombre) parts.push(d.nombre as string);
  if (d.campos) parts.push(`Campos: ${(d.campos as string[]).join(', ')}`);
  if (d.estadoAnterior && d.estadoNuevo) parts.push(`${d.estadoAnterior} → ${d.estadoNuevo}`);
  if (d.accion) parts.push(d.accion as string);
  if (d.comentario) parts.push(`"${d.comentario}"`);
  return parts.length > 0 ? parts.join(' · ') : JSON.stringify(d);
}

const PAGE_SIZE = 50;

// ═══════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════
export function AuditoriaPage({ embedded = false }: { embedded?: boolean }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const actionLabels = getActionLabels(t.audit.actions);

  // ─── State ───
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [entidadFilter, setEntidadFilter] = useState('');
  const [accionFilter, setAccionFilter] = useState('');
  const [desdeFilter, setDesdeFilter] = useState('');
  const [hastaFilter, setHastaFilter] = useState('');

  // Filter options
  const [entidades, setEntidades] = useState<string[]>([]);
  const [acciones, setAcciones] = useState<string[]>([]);

  // ─── Load filter options ───
  useEffect(() => {
    fetchAuditoriaEntidades()
      .then((r) => setEntidades(r.data))
      .catch(() => {});
    fetchAuditoriaAcciones()
      .then((r) => setAcciones(r.data))
      .catch(() => {});
  }, []);

  // ─── Load data ───
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: AuditoriaParams = { page, limit: PAGE_SIZE };
      if (entidadFilter) params.entidad = entidadFilter;
      if (accionFilter) params.accion = accionFilter;
      if (desdeFilter) params.desde = desdeFilter;
      if (hastaFilter) params.hasta = hastaFilter;

      const result = await fetchAuditoria(params);
      setLogs(result.data);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (err: any) {
      setError(err.message ?? t.audit.errorLoading);
    } finally {
      setLoading(false);
    }
  }, [page, entidadFilter, accionFilter, desdeFilter, hastaFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset page on filter change
  const applyFilter = useCallback((setter: (v: string) => void, value: string) => {
    setter(value);
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setEntidadFilter('');
    setAccionFilter('');
    setDesdeFilter('');
    setHastaFilter('');
    setPage(1);
  }, []);

  const hasFilters = entidadFilter || accionFilter || desdeFilter || hastaFilter;

  // ─── Styles ───
  const selectStyle: React.CSSProperties = {
    padding: '6px 10px',
    fontSize: 13,
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    backgroundColor: '#fff',
    color: C.text,
    fontFamily: 'inherit',
    minWidth: 150,
  };

  const inputStyle: React.CSSProperties = {
    ...selectStyle,
    minWidth: 130,
  };

  const thStyle: React.CSSProperties = {
    padding: '10px 14px',
    fontSize: 11,
    fontWeight: 700,
    color: '#fff',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    textAlign: 'left' as const,
    backgroundColor: C.azulMuyOscuro,
    whiteSpace: 'nowrap' as const,
  };

  const tdStyle: React.CSSProperties = {
    padding: '10px 14px',
    fontSize: 13,
    color: C.text,
    borderBottom: `1px solid ${C.border}`,
    verticalAlign: 'top' as const,
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: C.bg,
        fontFamily: 'inherit',
      }}
    >
      {!embedded && <AppHeader modulo="Compliance" titulo="Auditoría Documental" />}

      {/* ═══ CONTENT ═══ */}
      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        {/* ═══ FILTERS BAR ═══ */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            padding: '12px 16px',
            backgroundColor: '#fff',
            borderRadius: 10,
            border: `1px solid ${C.border}`,
            flexWrap: 'wrap' as const,
          }}
        >
          <Ic d={ICON_PATHS.filter} s={16} c={C.textSec} />

          <select
            value={entidadFilter}
            onChange={(e) => applyFilter(setEntidadFilter, e.target.value)}
            style={selectStyle}
          >
            <option value="">{t.audit.allEntities}</option>
            {entidades.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>

          <select
            value={accionFilter}
            onChange={(e) => applyFilter(setAccionFilter, e.target.value)}
            style={selectStyle}
          >
            <option value="">{t.audit.allActions}</option>
            {acciones.map((a) => (
              <option key={a} value={a}>
                {getActionConfig(a, actionLabels).label}
              </option>
            ))}
          </select>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: C.textSec,
            }}
          >
            {t.audit.fromDate}
            <input
              type="date"
              value={desdeFilter}
              onChange={(e) => applyFilter(setDesdeFilter, e.target.value)}
              style={inputStyle}
            />
          </label>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: C.textSec,
            }}
          >
            {t.audit.toDate}
            <input
              type="date"
              value={hastaFilter}
              onChange={(e) => applyFilter(setHastaFilter, e.target.value)}
              style={inputStyle}
            />
          </label>

          {hasFilters && (
            <button
              onClick={clearFilters}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '6px 12px',
                borderRadius: 6,
                border: `1px solid ${C.border}`,
                backgroundColor: '#fff',
                cursor: 'pointer',
                fontSize: 12,
                color: C.textSec,
                fontFamily: 'inherit',
              }}
            >
              <Ic d={ICON_PATHS.x} s={12} c={C.textSec} />
              {t.audit.clearFilters}
            </button>
          )}

          <div style={{ flex: 1 }} />

          <button
            onClick={loadData}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 6,
              border: `1px solid ${C.border}`,
              backgroundColor: '#fff',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 500,
              color: C.text,
              fontFamily: 'inherit',
            }}
          >
            <Ic d={ICON_PATHS.redo} s={14} c={C.textSec} />
            {t.audit.refresh}
          </button>
        </div>

        {/* ═══ TABLE ═══ */}
        {error ? (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              color: '#DC2626',
              backgroundColor: '#FEF2F2',
              borderRadius: 10,
              border: '1px solid #FECACA',
            }}
          >
            {error}
          </div>
        ) : (
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: 10,
              border: `1px solid ${C.border}`,
              overflow: 'hidden',
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>{t.audit.date}</th>
                    <th style={thStyle}>{t.audit.user}</th>
                    <th style={thStyle}>{t.audit.action}</th>
                    <th style={thStyle}>{t.audit.entity}</th>
                    <th style={{ ...thStyle, minWidth: 250 }}>{t.audit.detail}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{ ...tdStyle, textAlign: 'center', padding: 40, color: C.textSec }}
                      >
                        {t.audit.loadingRecords}
                      </td>
                    </tr>
                  ) : logs.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{ ...tdStyle, textAlign: 'center', padding: 40, color: C.textSec }}
                      >
                        {t.audit.noRecordsFound}
                      </td>
                    </tr>
                  ) : (
                    logs.map((entry, idx) => {
                      const actionCfg = getActionConfig(entry.evento, actionLabels);
                      const isAlt = idx % 2 === 1;
                      return (
                        <tr key={entry.id} style={{ backgroundColor: isAlt ? '#F2F4F6' : '#fff' }}>
                          {/* Fecha */}
                          <td
                            style={{
                              ...tdStyle,
                              whiteSpace: 'nowrap',
                              fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                              fontSize: 12,
                            }}
                          >
                            {formatTimestamp(entry.creadoEn)}
                          </td>

                          {/* Usuario */}
                          <td style={tdStyle}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>
                              {entry.usuario.nombreCompleto ?? entry.usuario.nombre}
                            </div>
                            <div style={{ fontSize: 11, color: C.textSec }}>
                              {entry.usuario.email}
                            </div>
                          </td>

                          {/* Acción (badge) */}
                          <td style={tdStyle}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                padding: '3px 10px',
                                borderRadius: 20,
                                fontSize: 11,
                                fontWeight: 600,
                                color: actionCfg.color,
                                backgroundColor: actionCfg.bg,
                                border: `1px solid ${actionCfg.color}22`,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <span
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  backgroundColor: actionCfg.color,
                                }}
                              />
                              {actionCfg.label}
                            </span>
                          </td>

                          {/* Entidad */}
                          <td style={tdStyle}>
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: C.azul2,
                                fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                              }}
                            >
                              {entry.entidad}
                            </span>
                            {Boolean(entry.detalle?.codigo) && (
                              <span
                                onClick={() => {
                                  if (entry.entidad === 'Documento' && entry.detalle?.codigo) {
                                    navigate(`/documento/${entry.detalle.codigo}`);
                                  }
                                }}
                                style={{
                                  display: 'block',
                                  fontSize: 11,
                                  color: C.azulClaro,
                                  cursor: entry.entidad === 'Documento' ? 'pointer' : 'default',
                                  marginTop: 2,
                                }}
                              >
                                {String(entry.detalle?.codigo ?? '')}
                              </span>
                            )}
                          </td>

                          {/* Detalle */}
                          <td style={{ ...tdStyle, fontSize: 12, color: C.textSec, maxWidth: 400 }}>
                            <span
                              style={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatDetalle(entry)}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ═══ PAGINATION ═══ */}
            {totalPages > 1 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  borderTop: `1px solid ${C.border}`,
                  backgroundColor: '#FAFBFC',
                }}
              >
                <span style={{ fontSize: 13, color: C.textSec }}>
                  {t.audit.pageOf(page, totalPages, total)}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    style={{
                      padding: '6px 14px',
                      fontSize: 13,
                      borderRadius: 6,
                      border: `1px solid ${C.border}`,
                      backgroundColor: page <= 1 ? '#F3F4F6' : '#fff',
                      color: page <= 1 ? C.textSec : C.text,
                      cursor: page <= 1 ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {t.audit.previous}
                  </button>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    style={{
                      padding: '6px 14px',
                      fontSize: 13,
                      borderRadius: 6,
                      border: `1px solid ${C.border}`,
                      backgroundColor: page >= totalPages ? '#F3F4F6' : '#fff',
                      color: page >= totalPages ? C.textSec : C.text,
                      cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {t.audit.next}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AuditoriaPage;
